import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api.js";
import type {
  Column,
  CreateTaskRequest,
  Task,
  UpdateTaskRequest,
} from "@fjord/shared";

export function useCreateTask(options?: { onSuccess?: (task: Task) => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskRequest) => api.createTask(body),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.(task);
    },
  });
}

export function useUpdateTask(
  taskId: string,
  options?: { onSuccess?: () => void; onConflict?: () => void },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateTaskRequest) => api.updateTask(taskId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-events", taskId] });
      options?.onSuccess?.();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["task", taskId] });
        options?.onConflict?.();
      }
    },
  });
}

export function useDeleteTask(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.();
    },
  });
}

export function useAddComment(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.addComment(taskId, { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-events", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.();
    },
  });
}

export function useAddJournalEntry(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.addJournalEntry(taskId, { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-events", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.();
    },
  });
}

export function useAddBlocker(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (blockerId: string) => api.addBlocker(taskId, { blocker_id: blockerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.();
    },
  });
}

export function useRemoveBlocker(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (blockerId: string) => api.removeBlocker(taskId, blockerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.();
    },
  });
}

export function useArchiveTask(
  taskId: string,
  options?: { onSuccess?: () => void; onError?: (error: Error) => void },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.archiveTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["archived-tasks"] });
      options?.onSuccess?.();
    },
    onError: (err) => {
      options?.onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  });
}

export function useMoveTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; version: number; column: Column; position: number }) =>
      api.updateTask(args.id, {
        version: args.version,
        column: args.column,
        position: args.position,
      }),
    onMutate: (args) => {
      // The board reads ["tasks", spaceId] (see useTasks), so an exact-match
      // write to ["tasks"] would land on a cache entry nothing renders — the
      // move would only become visible after onSettled refetched, leaving the
      // card in its source column for a full network roundtrip (flicker on
      // drop). setQueriesData prefix-matches every ["tasks", *] list instead.
      //
      // Applied synchronously (no await before this point): handleDragEnd clears
      // the drag preview in the same tick, so the cache must already hold the
      // new position when React re-renders. cancelQueries is fired but not
      // awaited so it can't push the write into a later microtask.
      queryClient.cancelQueries({ queryKey: ["tasks"] });
      const previous = queryClient.getQueriesData<Task[]>({ queryKey: ["tasks"] });
      queryClient.setQueriesData<Task[]>({ queryKey: ["tasks"] }, (old) =>
        old?.map((t) =>
          t.id === args.id
            ? { ...t, column: args.column, position: args.position }
            : t,
        ) ?? old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useUpdateEvent(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, body }: { eventId: string; body: string }) =>
      api.updateEvent(taskId, eventId, { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-events", taskId] });
      options?.onSuccess?.();
    },
  });
}

export function useDeleteEvent(taskId: string, options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => api.deleteEvent(taskId, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-events", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options?.onSuccess?.();
    },
  });
}

// taskId is passed at mutate() time since ArchiveView iterates over many tasks
export function useUnarchiveTask(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.unarchiveTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["archived-tasks"] });
      options?.onSuccess?.();
    },
    onError: (err) => {
      options?.onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  });
}
